import { ColumnDef } from "@tanstack/react-table";
import * as badge from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format, differenceInDays, formatDistanceToNow } from "date-fns";
import { enUS } from "date-fns/locale";
import {
  Building2,
  User,
  Calendar,
  DollarSign,
  Check,
  X,
  Clock,
  FileText,
  MoreHorizontal,
  Eye,
  Trash2,
  RefreshCw,
  Pencil,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link, useNavigate } from "react-router-dom";
import { UserTooltip } from "@/components/shared/UserTooltip/UserTooltip";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { LeaseWithRelations } from "../types/lease";
import { LeaseStatus } from "@/common/api/services/leases";

const getStatusColor = (status: LeaseStatus) => {
  switch (status) {
    case "ACTIVE":
      return "bg-green-100 text-green-800 hover:bg-green-200";
    case "COMPLETED":
      return "bg-blue-100 text-blue-800 hover:bg-blue-200";
    case "CANCELED":
      return "bg-red-100 text-red-800 hover:bg-red-200";
    case "PENDING":
      return "bg-yellow-100 text-yellow-800 hover:bg-yellow-200";
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-200";
  }
};

const getStatusIcon = (status: LeaseStatus) => {
  switch (status) {
    case "ACTIVE":
      return <Check className="h-3.5 w-3.5 mr-1.5" />;
    case "COMPLETED":
      return <FileText className="h-3.5 w-3.5 mr-1.5" />;
    case "CANCELED":
      return <X className="h-3.5 w-3.5 mr-1.5" />;
    case "PENDING":
      return <Clock className="h-3.5 w-3.5 mr-1.5" />;
    default:
      return null;
  }
};

type LeaseColumnProps = {
  t: (key: string, options?: any) => string;
  onStatusChange: (leaseUUID: string, status: LeaseStatus) => void;
  onView: (leaseUUID: string) => void;
  onEdit: (leaseUUID: string) => void;
  onDelete: (leaseUUID: string) => void;
  hidePropertyColumn?: boolean;
  propertyUUID?: string;
};

export const getLeaseColumns = ({
  t,
  onStatusChange,
  onView,
  onEdit,
  onDelete,
  hidePropertyColumn = false,
  propertyUUID,
}: LeaseColumnProps): ColumnDef<LeaseWithRelations>[] => {
  let columns: ColumnDef<LeaseWithRelations>[] = [
    {
      accessorKey: "dates",
      header: () => t("leases.table.columns.dates"),
      cell: ({ row }) => {
        const lease = row.original;
        const daysLeft = lease.endDate
          ? differenceInDays(new Date(lease.endDate), new Date())
          : 0;

        // Визначаємо URL для перегляду оренди в залежності від контексту
        const viewUrl = propertyUUID
          ? `/home/properties/${propertyUUID}/leases/${lease.leaseUUID}`
          : `/home/properties/${lease.propertyUUID}/leases/${lease.leaseUUID}`;

        return (
          <Link
            to={viewUrl}
            className="block hover:bg-muted/50 rounded-md p-2 transition-colors"
          >
            <div className="flex flex-col space-y-1.5 whitespace-nowrap">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {format(new Date(lease.startDate), "dd MMM yyyy", {
                    locale: enUS,
                  })}
                  {lease.endDate &&
                    ` - ${format(new Date(lease.endDate), "dd MMM yyyy", { locale: enUS })}`}
                </span>
              </div>
              {lease.endDate && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className={`text-xs px-2.5 py-1 rounded-full w-fit ${daysLeft < 30
                          ? "bg-red-100 text-red-800"
                          : daysLeft < 90
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                          }`}
                      >
                        {daysLeft > 0
                          ? t("leases.daysLeft", { days: daysLeft })
                          : t("leases.expired")}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {daysLeft > 0
                        ? t("leases.expiresIn", { days: daysLeft })
                        : t("leases.expiredAgo", {
                          time: formatDistanceToNow(new Date(lease.endDate), {
                            locale: enUS,
                          }),
                        })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {lease.autoRenew && (
                <div className="flex items-center text-xs text-green-600 font-medium">
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  <span>{t("leases.autoRenew")}</span>
                </div>
              )}
            </div>
          </Link>
        );
      },
    },
    {
      accessorKey: "rent",
      header: () => t("leases.table.columns.rent"),
      cell: ({ row }) => {
        const lease = row.original;

        return (
          <div className="flex flex-col whitespace-nowrap">
            <div className="flex items-center">
              <DollarSign className="h-3.5 w-3.5 text-gray-500" />
              <span className="font-medium">
                {lease.monthlyRent.toLocaleString()}{" "}
                {lease.property?.currency || t("common.currency")}
              </span>
            </div>
            {(lease.securityDeposit ?? 0) > 0 && (
              <span className="text-xs text-gray-500">
                {t("leases.deposit")}:{" "}
                {(lease.securityDeposit ?? 0).toLocaleString()}{" "}
                {lease.property?.currency || t("common.currency")}
              </span>
            )}
            <span className="text-xs text-gray-500">
              {t("leases.paymentDay")}: {lease.paymentDay}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "tenant",
      header: () => t("leases.table.columns.tenant"),
      cell: ({ row }) => {
        const lease = row.original;

        if (!lease.tenant) {
          return (
            <div className="flex items-center text-gray-500">
              <User className="h-4 w-4 mr-1 opacity-50" />
              <span>{t("leases.noTenant")}</span>
            </div>
          );
        }

        return <UserTooltip userUUID={lease.tenantUUID!} />;
      },
    },
    {
      accessorKey: "manager",
      header: () => t("leases.table.columns.manager"),
      cell: ({ row }) => {
        const lease = row.original;

        if (!lease.manager) {
          return (
            <div className="flex items-center text-gray-500">
              <User className="h-4 w-4 mr-1 opacity-50" />
              <span>{t("leases.noManager")}</span>
            </div>
          );
        }

        return <UserTooltip userUUID={lease.managerUUID!} />;
      },
    },
    {
      accessorKey: "status",
      header: () => t("leases.table.columns.status"),
      cell: ({ row }) => {
        const lease = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <badge.Badge
                className={`flex items-center cursor-pointer ${getStatusColor(lease.status)}`}
              >
                {getStatusIcon(lease.status)}
                {t(`leases.status.${lease.status}`)}
              </badge.Badge>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => onStatusChange(lease.leaseUUID, "ACTIVE")}
              >
                <Check className="mr-2 h-4 w-4 text-green-600" />
                {t("leases.status.ACTIVE")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onStatusChange(lease.leaseUUID, "PENDING")}
              >
                <Clock className="mr-2 h-4 w-4 text-yellow-600" />
                {t("leases.status.PENDING")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onStatusChange(lease.leaseUUID, "COMPLETED")}
              >
                <FileText className="mr-2 h-4 w-4 text-blue-600" />
                {t("leases.status.COMPLETED")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onStatusChange(lease.leaseUUID, "CANCELED")}
              >
                <X className="mr-2 h-4 w-4 text-red-600" />
                {t("leases.status.CANCELED")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-xs text-gray-500">
                {t("leases.lastStatusChange")}:{" "}
                {formatDistanceToNow(new Date(lease.lastStatusChange), {
                  addSuffix: true,
                  locale: enUS,
                })}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
    {
      id: "actions",
      header: () => t("leases.table.columns.actions"),
      cell: ({ row }) => {
        const lease = row.original;

        return (
          <div className="text-right">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onView(lease.leaseUUID)}>
                  <Eye className="mr-2 h-4 w-4" />
                  {t("common.view")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit(lease.leaseUUID)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-red-600"
                  onClick={() => onDelete(lease.leaseUUID)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("common.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  // Якщо не потрібно приховувати колонку Property, додаємо її
  if (!hidePropertyColumn) {
    // Знаходимо індекс, куди вставити колонку Property (після rent і перед tenant)
    const insertIndex = 2;

    const propertyColumn: ColumnDef<LeaseWithRelations> = {
      accessorKey: "property",
      header: () => t("leases.table.columns.property"),
      cell: ({ row }) => {
        const lease = row.original;
        const icon = <Building2 className="w-8 h-8 text-gray-400" />;

        return (
          <Link
            to={`/home/properties/${lease.propertyUUID}`}
            className="flex items-center gap-3 cursor-pointer hover:bg-muted/50 rounded-md p-1 transition-colors"
          >
            <div className="flex items-center justify-center w-16 h-16 rounded-md bg-muted overflow-hidden">
              {lease.property?.images && lease.property.images.length > 0 ? (
                <img
                  src={lease.property.images[0].thumbnail.url}
                  alt={lease.property?.name || "Property"}
                  className="w-full h-full object-cover rounded-md"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/80x80?text=No+Image";
                  }}
                />
              ) : (
                icon
              )}
            </div>
            <div className="flex flex-col">
              <span className="font-medium truncate max-w-[220px]">
                {lease.property?.name || t("common.notSpecified")}
              </span>
              <span className="text-sm text-muted-foreground truncate max-w-[220px]">
                {lease.property?.address || lease.property?.city || "—"}
              </span>
            </div>
          </Link>
        );
      },
    };

    // Вставляємо колонку Property у правильну позицію
    columns.splice(insertIndex, 0, propertyColumn);
  }

  return columns;
};
